import React, { useState, useEffect, useRef } from 'react';
import { Moon } from 'lucide-react';
import { t } from '../i18n';
import { localizeError } from '../i18n/errors';
import { amountInputProps, centsToInputString, getBlindPresets, isValidBetAmount } from '../utils/currency';
import { useCurrency } from '../utils/currencyContext';
import { setAwayPlayersPayBlinds, setBlindsGoUpMinutes, updateRoomSettings } from '../utils/roomManager';
import { awayPlayersPayBlinds } from '../utils/awayBlinds';
import { blindsGoUpMinutes, minutesInputValue, parseBlindsGoUpInput } from '../utils/blindsGoUp';
import { TABLE_VIBES, CALLOUT_MODES, DEFAULT_TABLE_VIBE, DEFAULT_CALLOUT_MODE, TABLE_VIBE_EXAMPLES, CALLOUT_EXAMPLES } from '../utils/winMessages';
import Modal from './ui/Modal';
import Button from './ui/Button';
import InkSwitch from './ui/InkSwitch';
import AwayBlindsToggle from './game/AwayBlindsToggle';

const TABLE_VIBE_OPTIONS = [
  { value: TABLE_VIBES.CLASSIC,    labelKey: 'settings.vibe.classic' },
  { value: TABLE_VIBES.HYPE,       labelKey: 'settings.vibe.hype' },
  { value: TABLE_VIBES.TRASH_TALK, labelKey: 'settings.vibe.trashTalk' },
];

const CALLOUT_OPTIONS = [
  { value: CALLOUT_MODES.OFF,      labelKey: 'settings.callout.off' },
  { value: CALLOUT_MODES.FRIENDLY, labelKey: 'settings.callout.friendly' },
  { value: CALLOUT_MODES.ADULT,    labelKey: 'settings.callout.adult' },
];

const SEGMENT_BTN =
  'min-h-[34px] rounded-lg px-1.5 py-1.5 text-[12px] font-semibold border transition-colors';

function SettingsOverlay({
  gameState,
  currentPlayerId,
  onClose,
  blindsOnly = false,
  onToggleAwayPlayersPayBlinds,
}) {
  const { roomCode, settings } = gameState;
  const { formatCurrency, parseCurrencyInput, currency, locale, symbol } = useCurrency();
  const presets = getBlindPresets(currency);
  const showChangeBlindsAway = blindsOnly && typeof onToggleAwayPlayersPayBlinds === 'function';
  const showGameSettingsAway = !blindsOnly;

  const [smallBlind, setSmallBlind] = useState('');
  const [bigBlind, setBigBlind] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [tableVibe, setTableVibe] = useState(DEFAULT_TABLE_VIBE);
  const [callouts, setCallouts] = useState(DEFAULT_CALLOUT_MODE);
  const [awayPay, setAwayPay] = useState(() => awayPlayersPayBlinds(settings));
  const [goUpOn, setGoUpOn] = useState(() => blindsGoUpMinutes(settings) > 0);
  const [goUpInput, setGoUpInput] = useState(() => minutesInputValue(blindsGoUpMinutes(settings)));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const goUpMinutesRef = useRef(null);
  const focusGoUpOnEnable = useRef(false);

  useEffect(() => {
    if (settings) {
      const list = getBlindPresets(currency);
      setSmallBlind(centsToInputString(settings.smallBlind, currency, locale));
      setBigBlind(centsToInputString(settings.bigBlind, currency, locale));
      const matchIndex = list.findIndex(
        p => p.sb === settings.smallBlind && p.bb === settings.bigBlind
      );
      setSelectedPreset(matchIndex >= 0 ? matchIndex : null);
      setTableVibe(settings.tableVibe || DEFAULT_TABLE_VIBE);
      setCallouts(settings.callouts || DEFAULT_CALLOUT_MODE);
      setAwayPay(awayPlayersPayBlinds(settings));
      const minutes = blindsGoUpMinutes(settings);
      setGoUpOn(minutes > 0);
      setGoUpInput(minutesInputValue(minutes));
    }
  }, [settings, currency, locale]);

  useEffect(() => {
    if (!focusGoUpOnEnable.current || !goUpOn) return;
    focusGoUpOnEnable.current = false;
    goUpMinutesRef.current?.focus();
  }, [goUpOn]);

  const handlePresetSelect = (index) => {
    const preset = presets[index];
    setSelectedPreset(index);
    setSmallBlind(centsToInputString(preset.sb, currency, locale));
    setBigBlind(centsToInputString(preset.bb, currency, locale));
    setError('');
  };

  const handleSmallBlindChange = (e) => {
    setSmallBlind(e.target.value);
    setSelectedPreset(null);
  };

  const handleBigBlindChange = (e) => {
    setBigBlind(e.target.value);
    setSelectedPreset(null);
  };

  const handleGoUpChange = (e) => {
    if (!goUpOn) return;
    const digits = String(e.target.value || '').replace(/\D/g, '').slice(0, 3);
    setGoUpInput(digits);
    setError('');
  };

  const handleGoUpToggle = (on) => {
    setGoUpOn(on);
    setError('');
    if (on && !String(goUpInput || '').trim()) {
      focusGoUpOnEnable.current = true;
    } else if (!on) {
      goUpMinutesRef.current?.blur();
    }
  };

  const handleAwayToggle = async (enabled) => {
    const previous = awayPay;
    setAwayPay(enabled);
    try {
      if (typeof onToggleAwayPlayersPayBlinds === 'function') {
        await onToggleAwayPlayersPayBlinds(enabled);
      } else {
        await setAwayPlayersPayBlinds(roomCode, currentPlayerId, enabled);
      }
    } catch (err) {
      console.error('Failed to update away-blinds setting:', err);
      setAwayPay(previous);
    }
  };

  const doSave = async () => {
    if (!smallBlind || !bigBlind) {
      setError(t('error.bothBlinds'));
      return;
    }
    let smallCents;
    let bigCents;
    try {
      smallCents = parseCurrencyInput(String(smallBlind));
      bigCents = parseCurrencyInput(String(bigBlind));
    } catch (err) {
      setError(localizeError(err.message));
      return;
    }
    if (!isValidBetAmount(smallCents) || !isValidBetAmount(bigCents)) {
      setError(t('error.validBlinds'));
      return;
    }
    if (smallCents >= bigCents) {
      setError(t('error.sbLessThanBb'));
      return;
    }
    let goUpMinutes = 0;
    if (goUpOn) {
      const goUp = parseBlindsGoUpInput(goUpInput);
      if (!goUp.ok || goUp.minutes < 1) {
        setError(t('error.enterMinutes'));
        goUpMinutesRef.current?.focus();
        return;
      }
      goUpMinutes = goUp.minutes;
    }
    setLoading(true);
    setError('');
    try {
      // Merge over existing settings — updateRoomSettings replaces the whole
      // settings node, so a partial object would wipe maxPlayers/enableRebuys.
      // blindsOnly keeps vibe/callouts from the live settings object (and
      // local state initialized from it) so this path never wipes them.
      await setBlindsGoUpMinutes(roomCode, currentPlayerId, goUpMinutes);
      const nextSettings = {
        ...settings,
        smallBlind: smallCents,
        bigBlind: bigCents,
        tableVibe,
        callouts,
        awayPlayersPayBlinds: awayPay,
        blindsGoUpMinutes: goUpMinutes,
      };
      if (goUpMinutes === 0) delete nextSettings.blindsUpStartedAt;
      await updateRoomSettings(
        roomCode,
        nextSettings,
        currentPlayerId
      );
      onClose();
    } catch (err) {
      setError(localizeError(err.message));
    } finally {
      setLoading(false);
    }
  };

  const vibeExample = TABLE_VIBE_EXAMPLES[tableVibe] || TABLE_VIBE_EXAMPLES[DEFAULT_TABLE_VIBE];
  const calloutExample = callouts === CALLOUT_MODES.OFF
    ? t('settings.emoteOff')
    : `\u201c${CALLOUT_EXAMPLES[callouts]}\u201d`;

  return (
    <Modal
      open
      compact
      onClose={onClose}
      dismissible={!loading}
      title={blindsOnly ? t('settings.changeBlinds') : t('lobby.gameSettings')}
      testId={blindsOnly ? 'change-blinds' : 'game-settings'}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={doSave}
            disabled={loading || !smallBlind || !bigBlind}
          >
            {loading ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="settings-stack flex flex-col gap-3">
        {blindsOnly && (
          <p className="text-[12px] text-mute leading-snug">{t('settings.nextHandEffect')}</p>
        )}
        {/* Preset cards */}
        <section>
          <div className="label-eyebrow text-mute mb-1.5">{t('settings.commonStakes')}</div>
          <div className="grid grid-cols-2 gap-1.5">
            {presets.map((preset, i) => {
              const selected = selectedPreset === i;
              return (
                <button
                  key={i}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => handlePresetSelect(i)}
                  className={`min-h-[36px] rounded-lg px-2 py-1.5 border transition-colors tnum text-[12px] font-semibold leading-tight ${
                    selected
                      ? 'bg-ink text-cream border-ink'
                      : 'bg-cream-2 text-ink border-line hover:border-ink'
                  }`}
                >
                  {t('poker.sb')} {formatCurrency(preset.sb)}
                  <span className={selected ? 'opacity-50' : 'text-mute'}> · </span>
                  {t('poker.bb')} {formatCurrency(preset.bb)}
                </button>
              );
            })}
          </div>
        </section>

        {/* Custom inputs */}
        <section className="border-t border-line-2 pt-3">
          <div className="label-eyebrow text-mute mb-1.5">{t('settings.custom')}</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="settings-field-label block text-[11px] text-mute mb-1">{t('poker.smallBlind')}</label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-mute text-[13px] pointer-events-none tnum">{symbol}</span>
                <input
                  type="text"
                  {...amountInputProps(currency)}
                  value={smallBlind}
                  onChange={handleSmallBlindChange}
                  placeholder={centsToInputString(100, currency, locale)}
                  disabled={loading}
                  data-testid="settings-sb"
                  className="w-full pl-8 pr-2 py-2 bg-cream-2 border border-line rounded-lg text-[14px] text-ink placeholder:text-mute focus:outline-none focus:border-ink transition-colors tnum disabled:opacity-50"
                />
              </div>
            </div>
            <div>
              <label className="settings-field-label block text-[11px] text-mute mb-1">{t('poker.bigBlind')}</label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-mute text-[13px] pointer-events-none tnum">{symbol}</span>
                <input
                  type="text"
                  {...amountInputProps(currency)}
                  value={bigBlind}
                  onChange={handleBigBlindChange}
                  placeholder={centsToInputString(200, currency, locale)}
                  disabled={loading}
                  data-testid="settings-bb"
                  className="w-full pl-8 pr-2 py-2 bg-cream-2 border border-line rounded-lg text-[14px] text-ink placeholder:text-mute focus:outline-none focus:border-ink transition-colors tnum disabled:opacity-50"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-line-2 pt-3" data-testid="settings-blinds-go-up">
          <div className="flex items-center justify-between gap-3 mb-1.5">
            <div className="flex items-baseline justify-between gap-2 min-w-0 flex-1">
              <label className="label-eyebrow text-mute whitespace-nowrap" htmlFor="blinds-go-up-minutes">{t('settings.blindTimer')}</label>
              <span className="settings-side-caption text-[10.5px] text-mute leading-tight text-right">{t('settings.blindTimerHint')}</span>
            </div>
            <InkSwitch
              hideLabel
              enabled={goUpOn}
              onChange={handleGoUpToggle}
              disabled={loading}
              ariaLabel={t('settings.blindTimer')}
              testId="blinds-go-up-toggle"
            />
          </div>
          <div className="relative max-w-[148px]">
            <input
              ref={goUpMinutesRef}
              id="blinds-go-up-minutes"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              enterKeyHint="done"
              value={goUpInput}
              onChange={handleGoUpChange}
              placeholder="15"
              disabled={!goUpOn || loading}
              tabIndex={goUpOn ? 0 : -1}
              readOnly={!goUpOn}
              data-testid="blinds-go-up-minutes"
              className="w-full pl-3 pr-10 py-2 bg-cream-2 border border-line rounded-lg text-[14px] text-ink placeholder:text-mute focus:outline-none focus:border-ink transition-colors tnum disabled:opacity-50 disabled:pointer-events-none"
            />
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-mute text-[13px] pointer-events-none">{t('common.min')}</span>
          </div>
          {error && (
            <div
              className="text-[12px] text-accent-danger mt-1.5 leading-tight"
              data-testid="settings-error"
            >
              {error}
            </div>
          )}
        </section>

        {showChangeBlindsAway && (
          <div className="flex items-center gap-3 pt-1">
            <Moon className="w-[18px] h-[18px] text-mute flex-shrink-0" strokeWidth={1.75} aria-hidden />
            <div className="flex-1 min-w-0">
              <AwayBlindsToggle
                enabled={awayPay}
                onChange={handleAwayToggle}
                disabled={loading}
              />
            </div>
          </div>
        )}

        {/* Table Vibe — lobby / full settings only */}
        {!blindsOnly && (
        <section className="border-t border-line-2 pt-3">
          <div className="flex items-baseline justify-between gap-2 mb-1.5">
            <div className="label-eyebrow text-mute">{t('settings.tableVibe')}</div>
            <span className="settings-side-caption text-[10.5px] text-mute leading-tight text-right">{t('settings.winMessageHint')}</span>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {TABLE_VIBE_OPTIONS.map(option => {
              const selected = tableVibe === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setTableVibe(option.value)}
                  disabled={loading}
                  className={`${SEGMENT_BTN} ${
                    selected
                      ? 'bg-ink text-cream border-ink'
                      : 'bg-cream-2 text-ink border-line hover:border-ink'
                  }`}
                >
                  {t(option.labelKey)}
                </button>
              );
            })}
          </div>
          <p className="settings-example text-[11px] text-mute mt-1 italic leading-tight" data-testid="settings-example">&ldquo;{vibeExample}&rdquo;</p>
        </section>
        )}

        {/* Emotes — lobby / full settings only (settings.callouts id unchanged) */}
        {!blindsOnly && (
        <section className="border-t border-line-2 pt-3">
          <div className="flex items-baseline justify-between gap-2 mb-1.5">
            <div className="label-eyebrow text-mute">{t('settings.emotes')}</div>
            <span className="settings-side-caption text-[10.5px] text-mute leading-tight text-right">{t('settings.emoteHint')}</span>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {CALLOUT_OPTIONS.map(option => {
              const selected = callouts === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setCallouts(option.value)}
                  disabled={loading}
                  className={`${SEGMENT_BTN} ${
                    selected
                      ? 'bg-ink text-cream border-ink'
                      : 'bg-cream-2 text-ink border-line hover:border-ink'
                  }`}
                >
                  {t(option.labelKey)}
                </button>
              );
            })}
          </div>
          <p className="settings-example text-[11px] text-mute mt-1 leading-tight" data-testid="settings-example">
            <span className={callouts === CALLOUT_MODES.OFF ? '' : 'italic'}>{calloutExample}</span>
            {callouts === CALLOUT_MODES.ADULT && (
              <span className="text-[10.5px]">{t('settings.emoteAdult')}</span>
            )}
          </p>
        </section>
        )}

        {showGameSettingsAway && (
          <section className="border-t border-line-2 pt-3" data-testid="settings-away">
            <div className="flex items-center justify-between gap-3">
              <div className="label-eyebrow text-mute">{t('sitOut.toggleLabel')}</div>
              <AwayBlindsToggle
                hideLabel
                enabled={awayPay}
                onChange={handleAwayToggle}
                disabled={loading}
              />
            </div>
            <p className="settings-away-hint text-[11px] text-mute mt-1.5 leading-tight" data-testid="settings-away-hint">
              {t('settings.awayHint')}
            </p>
          </section>
        )}

      </div>
    </Modal>
  );
}

export default SettingsOverlay;
